import type { LayoutColumnsFit, LayoutColumnsProps } from './LayoutColumns.types'

/**
 * layout-columns — feature-blind horizontal arrangement container.
 *
 * The sibling of layout-stack: children placed side by side instead of
 * top-to-bottom, in source order, separated by one uniform gap. No padding,
 * border, or background of its own; holds no state, emits no events.
 *
 * `fit` is the caller's ONE decision and it states intent, not measurements:
 * whether the columns DIVIDE the host's width between them or each take their
 * OWN width and let the row grow with their number. Both rules are one grid
 * track definition, declared here once, so no caller writes column CSS at its
 * use site and retuning the widths is a one-file edit.
 *
 * THE INLINE GAP SEPARATES TWO COLUMNS OF THE SAME KIND, so it only has to do
 * the work no column edge is already doing. A 4–5rem gutter was what two lists
 * on the bare canvas needed to stop reading as one wide row; a column that
 * draws its own edge states the division itself, and the wide gutter then costs
 * a third track its place on the line. 2rem separates without spending that
 * room.
 */
const fits: Record<LayoutColumnsFit, string> = {
  // Equal tracks that absorb the host's width: the row ALWAYS reaches both
  // edges of its host, so a row of columns lines up with whatever region sits
  // above it instead of stopping short of it.
  //
  // The narrow end is what decides how many tracks share a line (the browser
  // counts at the max sizing function only when that is definite, and `1fr`
  // never is). Held at 16rem so three tracks plus two 2rem gutters (832px)
  // still fit the page column of a 1280px laptop (908px) — the tracks then
  // stretch to fill it. Raising it to 18rem cost the third column its place on
  // the line.
  content: 'flex flex-wrap items-start gap-6 [&>*]:w-fit [&>*]:max-w-full',
  share: 'grid-cols-[repeat(auto-fit,minmax(min(16rem,100%),1fr))] gap-y-8 sm:gap-x-8',
  // Each track between its narrowest readable width and its COMFORTABLE one, as
  // many per line as fit, the line centred in what is left over. The upper end
  // is the one that matters: the browser counts how many tracks fit at their
  // COMFORTABLE width — never their narrowest — so that number, plus the gaps,
  // is what decides whether a third column joins the line or drops below it.
  // Held at 17rem because three of those plus two 2rem gutters (880px) still
  // sit inside the page column of a 1280px laptop (908px); 19rem needed 976px
  // and dropped the third column. The lower end only keeps a track readable
  // when the host runs out of room.
  own: 'grid-cols-[repeat(auto-fit,minmax(min(16rem,100%),17rem))] justify-center gap-y-8 sm:gap-x-8',
}

export function LayoutColumns({ fit = 'share', children }: LayoutColumnsProps) {
  return <div className={`${fit === 'content' ? '' : 'grid'} ${fits[fit]}`}>{children}</div>
}
